 /* =========================================================
 🌐 API CLIENT (SAFE - NO KEYS IN FRONTEND)
 ========================================================= */

 const AI_API_URL = "/api/ai-fix"; // your backend endpoint

 /* =========================================================
 🧠 SAFE JSON EXTRACTION (UPGRADED + EXPORTED)
 ========================================================= */

 export function extractJSON(text: string): any {

   try {

     if (!text) return null;

     const cleaned = text
       .replace(/```json/g, "")
       .replace(/```/g, "")
       .trim();

     /* 🔥 TRY DIRECT PARSE */
     try {
       return JSON.parse(cleaned);
     } catch { /* error intentionally ignored */ }

     /* 🔥 FALLBACK: EXTRACT FIRST JSON BLOCK */
     const match = cleaned.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);

     if (match) {
       return JSON.parse(match[0]);
     }

     return null;

   } catch (err) {

     return null;

   }
 }

 /* =========================================================
 🧠 VALIDATE FILE STRUCTURE
 ========================================================= */

 function validateFiles(files: any[]): boolean {

   if (!Array.isArray(files)) return false;

   return files.every(
     (f) =>
       f &&
       typeof f.path === "string" &&
       f.path.length > 0 &&
       typeof f.content === "string"
   );
 }

 /* =========================================================
 🧠 SMART FILE TRIM (TOKEN CONTROL)
 ========================================================= */

 function trimFiles(files: any[], maxChars = 12000) {

   let total = 0;
   const result: any[] = [];

   for (const file of files) {

     if (!file?.content) continue;

     const size = file.content.length;

     if (total + size > maxChars) break;

     result.push(file);
     total += size;
   }

   return result;
 }

 /* =========================================================
 🧠 RETRY WRAPPER (IMPORTANT FOR RELIABILITY)
 ========================================================= */

 async function withRetry(
   fn: () => Promise<any>,
   retries = 2
 ): Promise<any> {

   try {
     return await fn();
   } catch (err) {

     if (retries <= 0) throw err;

     return withRetry(fn, retries - 1);
   }
 }

 /* =========================================================
 🔥 AI FIXER (PRODUCTION READY - SAFE)
 ========================================================= */

 export async function fixBuildErrors(
   files: any[],
   errorMessage: string
 ) {

   if (!files || !errorMessage) {
     throw new Error("Missing files or errorMessage");
   }

   const safeFiles = trimFiles(files);

   const prompt = `
 You are a senior React + Vite engineer.

 A project failed to build.

 ERROR:

 ${errorMessage}

 FILES:

 ${JSON.stringify(safeFiles)}

 TASK:

 Fix ALL issues so the project builds successfully.

 STRICT RULES:

 Return ONLY valid JSON
 Format: [{ "path": "...", "content": "..." }]
 No explanations
 No markdown
 Do NOT remove required files
 Ensure imports are correct
 Fix syntax errors
 `;

   const run = async () => {

     const response = await fetch(AI_API_URL, {
       method: "POST",
       headers: {
         "Content-Type": "application/json",
       },
       body: JSON.stringify({ prompt }),
     });

     if (!response.ok) {
       throw new Error("AI request failed");
     }

     const data = await response.json();

     const raw = data?.content;

     const parsed = extractJSON(raw);

     if (!parsed || !validateFiles(parsed)) {
       throw new Error("Invalid AI fix format");
     }

     return parsed;
   };

   return await withRetry(run);
 }